import { useState, useRef } from "react";
import UploadAgreementModal from "@components/UploadAgreementModal";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { selectMediaUploadAgreement, updateMediaUploadAgreement } from "@store/globalSlice";
import commonservice from "@apis/commonservice";

interface UseUploadAgreementReturn {
  /** 弹窗组件，需要在页面中渲染 */
  agreementModal: React.ReactNode;
  /** 显示协议弹窗并等待用户确认，返回 Promise */
  confirmAgreement: () => Promise<boolean>;
}

/**
 * 上传协议确认 hook
 * 在上传媒体文件前调用 confirmAgreement 方法
 * 如果用户已同意协议，直接返回 true
 * 如果用户未同意，显示弹窗，确认后调用接口并返回 true，取消返回 false
 */
const useUploadAgreement = (): UseUploadAgreementReturn => {
  const dispatch = useAppDispatch();
  const mediaUploadAgreement = useAppSelector(selectMediaUploadAgreement);
  const [visible, setVisible] = useState(false);
  const [agreementItems, setAgreementItems] = useState<string[]>([]);
  const [currentVersion, setCurrentVersion] = useState<string>("");
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirmAgreement = async () => {
    if (mediaUploadAgreement.agreed) {
      return Promise.resolve(true);
    }

    try {
      const { agreed, currentVersion, items }: any = await commonservice.getMediaAgreement();
      dispatch(updateMediaUploadAgreement({ agreed, currentVersion }));
      if (agreed) return true;

      setCurrentVersion(currentVersion);
      setAgreementItems(
        (items || [])
          .filter((i: { content?: string }) => typeof i.content === "string" && i.content.trim() !== "")
          .map((i: { content: string }) => i.content),
      );
      setVisible(true);
      // 等待用户在弹窗中点击「接受」或「取消」后再 resolve，否则上传逻辑会拿到 undefined 而中断
      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
      });
    } catch {
      setAgreementItems([]);
      setVisible(true);
      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
      });
    }
  };

  const handleAccept = async () => {
    setVisible(false);
    try {
      // 调用接口确认协议
      await commonservice.confirmMediaAgreement({ version: currentVersion });
      // 更新本地状态
      dispatch(updateMediaUploadAgreement({ agreed: true, currentVersion }));
      resolveRef.current?.(true);
    } catch (error) {
      console.error("Failed to confirm media agreement:", error);
      resolveRef.current?.(false);
    }
    resolveRef.current = null;
  };

  const handleCancel = () => {
    setVisible(false);
    resolveRef.current?.(false);
    resolveRef.current = null;
  };

  const agreementModal = (
    <UploadAgreementModal
      open={visible}
      agreementItems={agreementItems}
      onAccept={handleAccept}
      onCancel={handleCancel}
    />
  );

  return {
    agreementModal,
    confirmAgreement,
  };
};

export default useUploadAgreement;
